import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text } from '@chakra-ui/react'
import { colors } from '../../../../utilities/colors'
import Spinner from '../../../common/Spinner'
import AddressFields from '../AddressFields'
import AuctionList from './AuctionList'
import SegmentedPicker from './SegmentedPicker'
import useStore from '../../../../store'
import { emptyLocation } from '../../../../constants/carpoolLocation'

const LocationStep = forwardRef(function LocationStep({ kind, value, onChange, accentColor, suggestion, excludedValue, unitCount = 1 }, ref) {
  const { t } = useTranslation()
  const { auctions, isFetchingAuctions, dealer, carpoolDealerPlaces, searchAuctions } = useStore()
  const [mode, setMode] = useState(null)
  const addressFieldsRef = useRef(null)

  useImperativeHandle(ref, () => ({
    isValid: () => {
      if (mode === 'manual') return addressFieldsRef.current?.isValid() ?? false
      return !!value?.postal_code
    },
  }))

  const dealerLot = useMemo(() => {
    const attrs = dealer?.attributes
    if (!attrs) return null
    const [cityFromCombined, stateFromCombined] = attrs['city-state']
      ? attrs['city-state'].split(',').map((part) => part.trim())
      : []
    const city = attrs['primary-city'] || cityFromCombined
    const state = attrs['primary-state'] || stateFromCombined
    const postal_code = attrs['primary-zip'] || attrs.zip
    if (!city || !state || !postal_code) return null
    return {
      id: attrs['primary-place-id'] ?? null,
      name: attrs.name || 'My Lot',
      address: attrs['primary-address1'] || '',
      city,
      state,
      postal_code,
      isPrimary: true,
      place_type: 'dealer',
    }
  }, [dealer])

  const excludedAuctionId = excludedValue?.type === 'Auction' ? excludedValue.id : null
  const myLocationsList = [dealerLot, ...(carpoolDealerPlaces || [])].filter(Boolean)
  const excludedIsPrimary = excludedValue?.type === 'DealerPlace' && excludedValue.isPrimary
  const excludedDealerPlaceId = excludedValue?.type === 'DealerPlace' ? excludedValue.id : null
  const filteredMyLocations = myLocationsList.filter((loc) => {
    if (excludedIsPrimary && loc.isPrimary) return false
    if (excludedDealerPlaceId && String(loc.id) === String(excludedDealerPlaceId)) return false
    return true
  })
  const hasMyLocations = filteredMyLocations.length > 0
  const selectedMyLocationId = value?.type === 'DealerPlace' && filteredMyLocations.some((p) => String(p.id) === String(value.id))
    ? value.id
    : null

  const label = kind === 'origin' ? t('Origin') : t('Destination')

  const resolvedSuggestion = useMemo(() => {
    if (!suggestion) return null
    if (suggestion.mode === 'lot') return { mode: 'my' }
    if (suggestion.mode === 'auction') {
      const match = auctions.find((a) => String(a.id) === String(suggestion.auctionId))
      if (match) return { mode: 'auction', auction: match }
    }
    return null
  }, [suggestion, auctions])

  const applyLocation = (loc, source) => {
    onChange({
      name: loc.name || '',
      city: loc.city || '',
      state: loc.state || '',
      postal_code: loc.postal_code || '',
      id: loc.id,
      type: source,
      place_type: source === 'DealerPlace' ? (loc.place_type || 'dealer') : null,
      isPrimary: source === 'DealerPlace' ? !!loc.isPrimary : false,
    })
  }

  useEffect(() => {
    if (mode || isFetchingAuctions) return
    let next

    // If value already has a selection (e.g. user came back), match its mode
    if (value?.type === 'Auction') next = 'auction'
    else if (value?.type === 'DealerPlace') next = 'my'
    else if (value?.postal_code) next = 'manual'
    // Otherwise pick a default — auction tab is always available for search
    else if (resolvedSuggestion) next = resolvedSuggestion.mode
    else if (kind === 'origin') next = 'auction'
    else next = hasMyLocations ? 'my' : 'auction'

    setMode(next)
  }, [mode, kind, hasMyLocations, isFetchingAuctions, resolvedSuggestion])

  useEffect(() => {
    if (!mode || value?.postal_code) return
    if (mode === 'my') {
      if (filteredMyLocations.length === 1) applyLocation(filteredMyLocations[0], 'DealerPlace')
    } else if (mode === 'auction') {
      if (resolvedSuggestion?.mode === 'auction') applyLocation(resolvedSuggestion.auction, 'Auction')
      else if (auctions.length === 1) applyLocation(auctions[0], 'Auction')
    }
  }, [mode, auctions.length, resolvedSuggestion])

  const handleModeChange = (newMode) => {
    if (newMode === mode) return
    setMode(newMode)
    if (newMode === 'my') {
      if (filteredMyLocations.length === 1) applyLocation(filteredMyLocations[0], 'DealerPlace')
      else onChange({ ...emptyLocation })
    } else if (newMode === 'auction') {
      if (auctions.length === 1) applyLocation(auctions[0], 'Auction')
      else onChange({ ...emptyLocation })
    } else if (newMode === 'manual') {
      onChange({ ...emptyLocation })
    }
  }

  const handleManualChange = (manualValue) => {
    onChange({
      name: manualValue.name || '',
      city: manualValue.city || '',
      state: manualValue.state || '',
      postal_code: manualValue.postal_code || '',
      type: manualValue.type || null,
    })
  }

  const selectedAuctionId = value?.type === 'Auction' ? value.id : null

  const suggestionMatchesCurrent = resolvedSuggestion && mode === resolvedSuggestion.mode && (
    resolvedSuggestion.mode === 'my' ||
    (resolvedSuggestion.mode === 'auction' && String(selectedAuctionId) === String(resolvedSuggestion.auction.id))
  )

  if (isFetchingAuctions && !mode) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" w="full" py={10}>
        <Spinner />
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={3} w="full">
      <Text fontSize="sm" color={colors.grey300} textAlign="center">
        {kind === 'origin' ? t('Where are you shipping from?') : t('Where are you shipping to?')}
      </Text>

      <SegmentedPicker
        mode={mode}
        onChange={handleModeChange}
        accent={accentColor}
        kind={kind}
        hasAuctions={true}
        hasMyLocations={hasMyLocations}
      />

      {suggestionMatchesCurrent && (
        <Text fontSize="xs" color={colors.grey300} textAlign="center">
          {unitCount > 1
            ? t('Suggested based on your vehicles — tap another option to change.')
            : t('Suggested from your vehicle — tap another option to change.')}
        </Text>
      )}

      {mode === 'my' && (
        <AuctionList
          auctions={filteredMyLocations}
          selectedId={selectedMyLocationId}
          onSelect={(loc) => applyLocation(loc, 'DealerPlace')}
          accent={accentColor}
        />
      )}

      {mode === 'auction' && (
        <AuctionList
          auctions={auctions}
          selectedId={selectedAuctionId}
          onSelect={(a) => applyLocation(a, 'Auction')}
          accent={accentColor}
          excludedAuctionId={excludedAuctionId}
          onSearch={searchAuctions}
        />
      )}

      {mode === 'manual' && (
        <AddressFields
          ref={addressFieldsRef}
          label={label}
          values={value}
          onChange={handleManualChange}
          accentColor={accentColor}
        />
      )}
    </Box>
  )
})

export default LocationStep
